import { useEffect, useState } from "react";
import { Row, Col, Table, Spinner, Form, Button, Modal, Badge, Card, Pagination } from "react-bootstrap";
import { Link, useSearchParams } from 'react-router-dom';
import debounce from 'lodash.debounce';

export default function Proposicoes() {
    const [searchParams] = useSearchParams();
    const [proposicoes, setProposicoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mostrarResultados, setMostrarResultados] = useState(false);
    const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState(false);
    const [autores, setAutores] = useState([]);

    const currentYear = new Date().getFullYear();

    const getInitialFormData = () => {
        // Map URL type parameters to form values
        const urlType = searchParams.get('type') || '';
        let formType = '';

        if (urlType === 'projeto') formType = 'projetos';
        else if (urlType === 'indicacao') formType = 'indicacoes';
        else if (urlType === 'requerimento') formType = 'requerimentos';
        else formType = urlType; // Keep as is if already in plural form

        // Legacy URL parameters support: field-proposicoes and field-proposicoes-filtro
        let numeroValue = searchParams.get('number') || '';
        let buscaConteudoValue = '';
        let filtroAutorValue = searchParams.get('author') || '';
        const fieldProposicoes = searchParams.get('field-proposicoes');
        const fieldProposicoesFiltro = searchParams.get('field-proposicoes-filtro');
        const fieldAutores = searchParams.get('field-autores');
        const fieldTipoFiltro = searchParams.get('field-tipo-filtro');

        // Handle legacy parameters based on filtro type
        if (fieldProposicoes && fieldProposicoesFiltro) {
            if (fieldProposicoesFiltro === 'numero') {
                numeroValue = fieldProposicoes;
            } else if (fieldProposicoesFiltro === 'palavra') {
                buscaConteudoValue = fieldProposicoes;
            } else if (fieldProposicoesFiltro === 'autor') {
                filtroAutorValue = fieldProposicoes;
            }
        }

        // Handle field-autores parameter
        if (fieldAutores) {
            filtroAutorValue = fieldAutores;
        }

        // Handle field-tipo-filtro parameter (maps to tipo)
        if (fieldTipoFiltro) {
            formType = fieldTipoFiltro; // Already in plural form (projetos, indicacoes, requerimentos)
        }

        return {
            tipo: formType,
            numero: numeroValue,
            ano: searchParams.get('year') || currentYear.toString(),
            legislatura: '',
            filtroAutor: filtroAutorValue,
            filtroTexto: '', // Busca na ementa (frontend)
            buscaConteudo: buscaConteudoValue, // Busca no conteúdo completo (backend)
            dataInicio: '',
            dataFim: ''
        };
    };

    const [formData, setFormData] = useState(getInitialFormData());

    // dadosTextoFiltrados: após aplicar filtro de Autor e Texto (ementa)
    const [dadosTextoFiltrados, setDadosTextoFiltrados] = useState([]);
    // dadosFiltrados: o que realmente é exibido (após aplicar filtro de Tipo sobre os dadosTextoFiltrados)
    const [dadosFiltrados, setDadosFiltrados] = useState([]);

    const [filtroAtivo, setFiltroAtivo] = useState('todos');
    const [paginaAtual, setPaginaAtual] = useState(1);
    const itensPorPagina = 10;

    // Mapeamento de tipos para códigos tipoprop
    const tipoToTipoProp = {
        'projeto': 'p',
        'indicacao': 'i',
        'parecer': 'pc',
        'requerimento': 'r'
    };
    
    // Função para gerar URL de detalhes (novo sistema ou legado)
    const gerarUrlDetalhes = (item) => {
        // Se temos docid, usar novo sistema
        if (item.dados.docid) {
            const tipoPropCode = tipoToTipoProp[item.tipo] || item.tipo;
            return `/proposicoes/detalhes?docid=${item.dados.docid}&tipoprop=${tipoPropCode}`;
        }
        // Fallback para sistema legado
        return `/proposicoes/detalhes?tipo=${item.tipo}&numero=${item.dados.numero}&ano=${item.dados.ano}`;
    };

    const years = [];
    for (let year = currentYear; year >= 2000; year--) {
        years.push(year);
    }

    const realizarBusca = async () => {
        setLoading(true);
        setError('');
        setMostrarResultados(false);

        try {
            const tiposParaBuscar = formData.tipo ? [formData.tipo] : ['projetos', 'indicacoes', 'requerimentos'];
            let todosOsDados = [];

            // Check if we need to search across all legislatures
            const searchAllLegislaturas = formData.numero && !formData.ano && !formData.legislatura;
            const legislaturasParaBuscar = searchAllLegislaturas ? ['20', '19'] : [null];

            for (const tipo of tiposParaBuscar) {
                for (const leg of legislaturasParaBuscar) {
                    try {
                        const url = construirURL(tipo, leg);
                        const response = await fetch(url);

                        if (!response.ok) {
                            console.warn(`Erro ao buscar ${tipo}: ${response.status}`);
                            continue;
                        }

                        const xmlData = await response.text();
                        const dadosDoTipo = processarXMLParaTipo(xmlData, tipo);
                        todosOsDados = todosOsDados.concat(dadosDoTipo);
                    } catch (err) {
                        console.warn(`Erro ao processar ${tipo}:`, err);
                    }
                }
            }

            // Apply frontend filters for numero and ano when combined with legislatura
            if (formData.numero) {
                todosOsDados = todosOsDados.filter(item =>
                    item.dados.numero === formData.numero
                );
            }
            if (formData.ano && formData.legislatura) {
                // Only filter by year in frontend if legislatura was also used
                todosOsDados = todosOsDados.filter(item =>
                    item.dados.ano === formData.ano
                );
            }

            // Sort by newest first (date if available, then by year desc, then by number desc within same type)
            todosOsDados.sort((a, b) => {
                // Try to parse dates first
                const dataA = converterDataParaComparacao(a.dados.dataCriacao);
                const dataB = converterDataParaComparacao(b.dados.dataCriacao);

                if (dataA && dataB) {
                    return dataB - dataA; // Most recent date first
                }

                // Fallback to year, then within same type sort by number
                const anoA = parseInt(a.dados.ano) || 0;
                const anoB = parseInt(b.dados.ano) || 0;

                if (anoA !== anoB) {
                    return anoB - anoA; // Year descending
                }

                // Only compare numbers within same type
                if (a.tipo === b.tipo) {
                    const numeroA = parseInt(a.dados.numero) || 0;
                    const numeroB = parseInt(b.dados.numero) || 0;
                    return numeroB - numeroA; // Number descending within same type
                }

                // Different types, sort by type name
                return a.tipo.localeCompare(b.tipo);
            });

            setProposicoes(todosOsDados);
            setAutores(extrairAutoresUnicos(todosOsDados));
            setFiltroAtivo('todos');
            setPaginaAtual(1);
            // Aplica os filtros textuais e já prepara a lista exibida
            aplicarFiltros(todosOsDados);
            setMostrarResultados(true);
            
        } catch (error) {
            console.error('Erro na busca:', error);
            setError(`Erro ao buscar dados: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const construirURL = (tipo, forceLegislatura = null) => {
        let url = `https://dadosabertos.alepe.pe.gov.br/api/v1/proposicoes/${tipo}/`;
        let params = [];

        // If forceLegislatura is provided (for searching across all legislatures), use it
        if (forceLegislatura) {
            params.push(`legislatura=${forceLegislatura}`);
            if (formData.numero) {
                params.push(`numero=${formData.numero}`);
            }
        }
        // Priority: numero+ano takes precedence over everything
        else if (formData.numero && formData.ano) {
            params.push(`numero=${formData.numero}`);
            params.push(`ano=${formData.ano}`);
            // Don't add legislatura when searching by specific numero+ano
        } else if (formData.numero && formData.legislatura) {
            // When only numero and legislatura are provided
            params.push(`numero=${formData.numero}`);
            params.push(`legislatura=${formData.legislatura}`);
        } else if (formData.legislatura) {
            // When only legislatura is selected
            params.push(`legislatura=${formData.legislatura}`);
        } else if (formData.ano) {
            // When only year is selected
            params.push(`ano=${formData.ano}`);
        } else if (!formData.numero) {
            // Default: search by current year (only if numero is not specified)
            params.push(`ano=${currentYear}`);
        }

        // Adicionar parâmetro de busca por conteúdo completo (backend)
        if (formData.buscaConteudo && formData.buscaConteudo.trim()) {
            params.push(`texto=${encodeURIComponent(formData.buscaConteudo.trim())}`);
        }

        if (params.length > 0) {
            url += '?' + params.join('&');
        }

        return url;
    };

    const processarXMLParaTipo = (xmlData, tipoUrl) => {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlData, 'text/xml');
            
            console.log(`Processing ${tipoUrl}, XML length: ${xmlData.length}`);
            console.log(`Root element:`, xmlDoc.documentElement?.tagName);
            
            const errorElement = xmlDoc.querySelector('error message');
            if (errorElement) {
                console.warn(`Erro para tipo ${tipoUrl}: ${errorElement.textContent}`);
                return [];
            }
            
            let dadosDoTipo = [];
            // Convert plural to singular properly
            let tipoSingular;
            if (tipoUrl === 'indicacoes') {
                tipoSingular = 'indicacao';
            } else if (tipoUrl === 'requerimentos') {
                tipoSingular = 'requerimento';
            } else if (tipoUrl === 'projetos') {
                tipoSingular = 'projeto';
            } else {
                // Fallback to old method
                tipoSingular = tipoUrl.slice(0, -1);
            }
            
            console.log(`Looking for elements: ${tipoSingular}`);

            if (formData.numero && formData.ano) {
                const itemElement = xmlDoc.querySelector(tipoSingular);
                if (itemElement) {
                    dadosDoTipo = [{
                        xml: xmlData,
                        tipo: tipoSingular,
                        dados: extrairDadosXML(xmlData)
                    }];
                }
            } else {
                // Multiple propositions
                const itemElements = xmlDoc.querySelectorAll(tipoSingular);
                
                console.log(`Found ${itemElements.length} elements for ${tipoSingular}`);
                console.log(`First few elements:`, Array.from(itemElements).slice(0, 3).map(el => el.outerHTML.substring(0, 200)));
                
                for (let element of itemElements) {
                    const ementaDireto = element.getAttribute('ementa');
                    const autorElement = element.querySelector('autores autor');
                    const autorDireto = autorElement ? autorElement.getAttribute('nome') : null;

                    const xmlString = new XMLSerializer().serializeToString(element);
                    const dados = extrairDadosXML(xmlString);

                    if ((!dados.ementa || dados.ementa === 'Ementa não disponível') && ementaDireto) {
                        dados.ementa = ementaDireto;
                    }

                    if ((!dados.autor || dados.autor === 'N/A') && autorDireto) {
                        dados.autor = autorDireto;
                    }

                    dadosDoTipo.push({
                        xml: xmlString,
                        tipo: tipoSingular,
                        dados: dados
                    });
                }
            }
            
            console.log(`Returning ${dadosDoTipo.length} items for ${tipoUrl}`);
            return dadosDoTipo;
            
        } catch (error) {
            console.error(`Erro ao processar XML para ${tipoUrl}:`, error);
            return [];
        }
    };

    const extrairDadosXML = (xmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'text/xml');
        
        const mainElement = doc.querySelector('projeto') || doc.querySelector('indicacao') || doc.querySelector('requerimento');
        
        let dados = {
            numero: 'N/A',
            ano: 'N/A',
            ementa: 'Ementa não disponível',
            situacao: 'N/A',
            autor: 'N/A',
            dataCriacao: 'N/A',
            tipo: 'N/A',
            subtipo: 'N/A',
            legislatura: 'N/A',
            docid: null
        };
        
        if (mainElement) {
            dados.numero = mainElement.getAttribute('numero') || 'N/A';
            dados.ano = mainElement.getAttribute('ano') || 'N/A';
            dados.tipo = mainElement.getAttribute('tipo') || 'N/A';
            dados.subtipo = mainElement.getAttribute('subtipo') || 'N/A';
            dados.legislatura = mainElement.getAttribute('legislatura') || 'N/A';
            dados.situacao = mainElement.getAttribute('situacao') || 'N/A';
            dados.docid = mainElement.getAttribute('docid');
            
            const autorElement = mainElement.querySelector('autores autor');
            if (autorElement) {
                dados.autor = autorElement.getAttribute('nome') || 'N/A';
            }
            
            dados.ementa = mainElement.getAttribute('ementa');
            if (!dados.ementa) {
                const ementaElement = mainElement.querySelector('ementa');
                if (ementaElement) {
                    dados.ementa = ementaElement.textContent.trim();
                } else {
                    const materiaElement = mainElement.querySelector('materia');
                    dados.ementa = materiaElement ? materiaElement.textContent.trim() : 'Ementa não disponível';
                }
            }

            dados.dataCriacao = mainElement.getAttribute('dataPublicacao') || 'N/A';
        }
        
        return dados;
    };

    const getElementText = (doc, tagName) => {
        const element = doc.querySelector(tagName);
        return element ? element.textContent.trim() : null;
    };

    const converterDataParaComparacao = (dataStr) => {
        if (!dataStr || dataStr === 'N/A') return null;
        
        const formatos = [
            /^(\d{4})-(\d{2})-(\d{2})/,
            /^(\d{2})\/(\d{2})\/(\d{4})/,
            /^(\d{4})\/(\d{2})\/(\d{2})/
        ];
        
        for (let formato of formatos) {
            const match = dataStr.match(formato);
            if (match) {
                let ano, mes, dia;
                if (formato.source.startsWith('^(\\d{4})')) {
                    ano = parseInt(match[1]);
                    mes = parseInt(match[2]) - 1;
                    dia = parseInt(match[3]);
                } else {
                    dia = parseInt(match[1]);
                    mes = parseInt(match[2]) - 1;
                    ano = parseInt(match[3]);
                }
                return new Date(ano, mes, dia);
            }
        }
        
        return null;
    };

    // Aplica filtros de Autor e Texto e depois aplica o filtro de Tipo atual sobre esse resultado
    const aplicarFiltros = (dadosBase) => {
        let arr = Array.isArray(dadosBase) ? [...dadosBase] : [...proposicoes];

        if (formData.filtroAutor) {
            arr = arr.filter(item =>
                item.dados.autor &&
                item.dados.autor.toLowerCase().includes(formData.filtroAutor.toLowerCase())
            );
        }

        if (formData.filtroTexto) {
            arr = arr.filter(item =>
                item.dados.ementa &&
                item.dados.ementa.toLowerCase().includes(formData.filtroTexto.toLowerCase())
            );
        }

        // Guarda a base após filtros textuais
        setDadosTextoFiltrados(arr);

        // Agora aplica o filtro de tipo atual sobre essa base
        const exibicao = filtroAtivo === 'todos'
            ? arr
            : arr.filter(item => item.tipo === filtroAtivo);

        setDadosFiltrados(exibicao);
        setPaginaAtual(1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleLegislaturaChange = (e) => {
        const { value } = e.target;
        if (value) {
            setFormData({ ...formData, legislatura: value, ano: '' });
        } else {
            setFormData({ ...formData, legislatura: value, ano: currentYear.toString() });
        }
    };

    const debouncedAplicarFiltros = debounce(() => {
        aplicarFiltros(proposicoes);
    }, 500);

    const handleFilterInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        debouncedAplicarFiltros();
    };

    const limparFormulario = () => {
        setFormData({
            tipo: '',
            numero: '',
            ano: '',
            legislatura: '',
            filtroAutor: '',
            filtroTexto: '',
            buscaConteudo: '',
            dataInicio: '',
            dataFim: ''
        });
        setMostrarFiltrosAvancados(false);
        setMostrarResultados(false);
        setError('');
        setProposicoes([]);
        setDadosTextoFiltrados([]);
        setDadosFiltrados([]);
        setAutores([]);
        setPaginaAtual(1);
        setFiltroAtivo('todos');
    };

    const filtrarTodos = () => {
        setFiltroAtivo('todos');
        setDadosFiltrados([...dadosTextoFiltrados]);
        setPaginaAtual(1);
    };

    const filtrarPorTipo = (tipo) => {
        setFiltroAtivo(tipo);
        const filtrados = dadosTextoFiltrados.filter(item => item.tipo === tipo);
        setDadosFiltrados(filtrados);
        setPaginaAtual(1);
    };

    const contarTipo = (tipo) => {
        return dadosTextoFiltrados.filter(item => item.tipo === tipo).length;
    };

    const getTipoNome = (tipo) => {
        const nomes = {
            'projeto': 'Projeto',
            'indicacao': 'Indicação',
            'requerimento': 'Requerimento'
        };
        return nomes[tipo] || tipo.charAt(0).toUpperCase() + tipo.slice(1);
    };

    const getTipoBadgeClass = (tipo) => {
        const classes = {
            'projeto': 'success',
            'indicacao': 'info',
            'requerimento': 'warning'
        };
        return classes[tipo] || 'secondary';
    };

    const formatarData = (data) => {
        if (!data || data === 'N/A') return 'N/A';
        
        const formatos = [
            /^(\d{4})-(\d{2})-(\d{2})/,
            /^(\d{2})\/(\d{2})\/(\d{4})/,
            /^(\d{4})\/(\d{2})\/(\d{2})/
        ];
        
        for (let formato of formatos) {
            const match = data.match(formato);
            if (match) {
                let dia, mes, ano;
                if (formato.source.startsWith('^(\\d{4})')) {
                    ano = match[1];
                    mes = match[2];
                    dia = match[3];
                } else {
                    dia = match[1];
                    mes = match[2];
                    ano = match[3];
                }
                return `${dia}/${mes}/${ano}`;
            }
        }
        
        return data;
    };

    const decodeHTMLEntities = (text) => {
        if (!text) return text;
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        const decoded = textarea.value;

        const div = document.createElement('div');
        div.innerHTML = decoded;
        const textContent = div.textContent || div.innerText || '';

        return textContent.replace(/\s+/g, ' ').trim();
    };

    const truncateText = (text, maxLength) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const extrairAutoresUnicos = (proposicoesData) => {
        const autoresUnicos = new Set();
        proposicoesData.forEach(proposicao => {
            if (proposicao.dados.autor && proposicao.dados.autor !== 'N/A' && proposicao.dados.autor.trim()) {
                autoresUnicos.add(proposicao.dados.autor.trim());
            }
        });
        return Array.from(autoresUnicos).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    };

    // Pagination
    const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina);
    const dadosPagina = dadosFiltrados.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

    const trocarPagina = (direction) => {
        if (direction === -1 && paginaAtual > 1) {
            setPaginaAtual(paginaAtual - 1);
        } else if (direction === 1 && paginaAtual < totalPaginas) {
            setPaginaAtual(paginaAtual + 1);
        }
    };

    const irParaPagina = (pagina) => {
        if (pagina >= 1 && pagina <= totalPaginas) {
            setPaginaAtual(pagina);
        }
    };

    const gerarNumerosPaginas = () => {
        const maxPaginasVisiveis = 5;
        const paginas = [];

        if (totalPaginas <= maxPaginasVisiveis) {
            for (let i = 1; i <= totalPaginas; i++) {
                paginas.push(i);
            }
        } else {
            let inicio = Math.max(1, paginaAtual - 2);
            let fim = Math.min(totalPaginas, paginaAtual + 2);

            if (fim - inicio + 1 < maxPaginasVisiveis) {
                if (inicio === 1) {
                    fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);
                } else {
                    inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
                }
            }

            if (inicio > 1) {
                paginas.push(1);
                if (inicio > 2) {
                    paginas.push('...');
                }
            }

            for (let i = inicio; i <= fim; i++) {
                paginas.push(i);
            }

            if (fim < totalPaginas) {
                if (fim < totalPaginas - 1) {
                    paginas.push('...');
                }
                paginas.push(totalPaginas);
            }
        }

        return paginas;
    };

    useEffect(() => {
        // Check if we have URL parameters to trigger automatic search
        const hasUrlParams = searchParams.get('type') || searchParams.get('number') || searchParams.get('author');

        if (hasUrlParams) {
            if (searchParams.get('author')) {
                setMostrarFiltrosAvancados(true);
            }
            setTimeout(() => realizarBusca(), 100);
        } else {
            setTimeout(() => realizarBusca(), 100);
        }
    }, []);

    return (
        <Row>
            {/* Search Form */}
            <Col xs="12">
                <Card className="mb-4">
                    <Card.Body>
                        <h5>Filtros de Busca</h5>
                        <Form onSubmit={(e) => { e.preventDefault(); realizarBusca(); }}>
                            <Row className="g-3">
                                <Col md={2}>
                                    <Form.Group controlId="tipo">
                                        <Form.Label>Tipo</Form.Label>
                                        <Form.Select
                                            name="tipo"
                                            value={formData.tipo}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Todos os tipos</option>
                                            <option value="projetos">📋 Projetos de Lei</option>
                                            <option value="indicacoes">📝 Indicações</option>
                                            <option value="requerimentos">📄 Requerimentos</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="numero">
                                        <Form.Label>Número</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="numero"
                                            value={formData.numero}
                                            onChange={handleInputChange}
                                            placeholder="Ex: 123"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="ano">
                                        <Form.Label>Ano</Form.Label>
                                        <Form.Select
                                            name="ano"
                                            value={formData.ano}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Selecione</option>
                                            {years.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group controlId="legislatura">
                                        <Form.Label>Legislatura</Form.Label>
                                        <Form.Select
                                            name="legislatura"
                                            value={formData.legislatura}
                                            onChange={handleLegislaturaChange}
                                        >
                                            <option value="">Selecione</option>
                                            <option value="20">XX - Vigésima (2023-2026)</option>
                                            <option value="19">XIX - Décima Nona (2019-2022)</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        onClick={realizarBusca}
                                        disabled={loading}
                                        className="w-100"
                                        style={{ height: '42px' }}
                                    >
                                        {loading ? 'Buscando...' : 'Buscar'}
                                    </Button>
                                </Col>
                            </Row>
                            
                            {/* Advanced Filters */}
                            <Row className="mt-3">
                                <Col xs="12" className="d-flex gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setMostrarFiltrosAvancados(!mostrarFiltrosAvancados)}
                                    >
                                        Filtros Avançados {mostrarFiltrosAvancados ? '▲' : '▼'}
                                    </Button>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={limparFormulario}
                                    >
                                        Limpar Tudo
                                    </Button>
                                </Col>
                            </Row>
                            
                            {mostrarFiltrosAvancados && (
                                <Row className="mt-3 p-3 bg-light rounded">
                                    <Col md={4}>
                                        <Form.Group controlId="filtroAutor">
                                            <Form.Label>Autor</Form.Label>
                                            <Form.Select
                                                name="filtroAutor"
                                                value={formData.filtroAutor}
                                                onChange={handleFilterInputChange}
                                            >
                                                <option value="">Todos os autores</option>
                                                {autores.map((autor, index) => (
                                                    <option key={index} value={autor}>
                                                        {autor}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group controlId="filtroTexto">
                                            <Form.Label>Buscar na Ementa</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="filtroTexto"
                                                value={formData.filtroTexto}
                                                onChange={handleFilterInputChange}
                                                placeholder="Buscar na ementa..."
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                            
                            {/* Busca de Conteúdo Completo - Seção Separada */}
                            <Row className="mt-4">
                                <Col xs="12">
                                    <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6', borderWidth: '2px' }}>
                                        <h6 className="mb-3" style={{ color: '#495057', fontWeight: '600' }}>
                                            <i className="bi bi-search"></i> Busca no Conteúdo Completo da Proposta Legislativa
                                            <small className="text-muted d-block" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                Busca em ementa, matéria, justificativa e demais textos
                                            </small>
                                        </h6>
                                        <Row>
                                            <Col md={8}>
                                                <Form.Group controlId="buscaConteudo">
                                                    <Form.Control
                                                        type="text"
                                                        name="buscaConteudo"
                                                        value={formData.buscaConteudo}
                                                        onChange={handleFilterInputChange}
                                                        placeholder="Digite o texto para buscar em todo o conteúdo da proposta legislativa..."
                                                        style={{ fontSize: '1.1em', padding: '10px' }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4} className="d-flex align-items-center">
                                                <Button 
                                                    variant="primary"
                                                    onClick={realizarBusca}
                                                    disabled={!formData.buscaConteudo?.trim() || loading}
                                                    style={{ padding: '10px 20px', fontWeight: '600', fontSize: '1.1em', height: '42px', margin: '0', border: 'none' }}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                                className="me-2"
                                                            />
                                                            Buscando...
                                                        </>
                                                    ) : (
                                                        'Buscar no Conteúdo'
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>

            {/* Loading */}
            {loading && (
                <Col xs="12" className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Buscando propostas legislativas...</span>
                    </Spinner>
                    <div className="mt-3">
                        <p className="text-muted">Buscando propostas legislativas...</p>
                    </div>
                </Col>
            )}

            {/* Results */}
            {mostrarResultados && !loading && (
                <>
                    {/* Quick Filters */}
                    {dadosTextoFiltrados.length > 0 && (
                        <Col xs="12" className="mb-3">
                            <Card>
                                <Card.Body>
                                    <h6>Filtros Rápidos</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Button
                                            variant={filtroAtivo === 'todos' ? 'primary' : 'outline-primary'}
                                            size="sm"
                                            onClick={filtrarTodos}
                                        >
                                            Todos ({dadosTextoFiltrados.length})
                                        </Button>
                                        {contarTipo('projeto') > 0 && (
                                            <Button
                                                variant={filtroAtivo === 'projeto' ? 'success' : 'outline-success'}
                                                size="sm"
                                                onClick={() => filtrarPorTipo('projeto')}
                                            >
                                                Projetos ({contarTipo('projeto')})
                                            </Button>
                                        )}
                                        {contarTipo('indicacao') > 0 && (
                                            <Button
                                                variant={filtroAtivo === 'indicacao' ? 'info' : 'outline-info'}
                                                size="sm"
                                                onClick={() => filtrarPorTipo('indicacao')}
                                            >
                                                Indicações ({contarTipo('indicacao')})
                                            </Button>
                                        )}
                                        {contarTipo('requerimento') > 0 && (
                                            <Button
                                                variant={filtroAtivo === 'requerimento' ? 'warning' : 'outline-warning'}
                                                size="sm"
                                                onClick={() => filtrarPorTipo('requerimento')}
                                            >
                                                Requerimentos ({contarTipo('requerimento')})
                                            </Button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}

                    {/* Results Table */}
                    {dadosFiltrados.length > 0 && (
                        <Col xs="12">
                            <div className="table-responsive">
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Número/Ano</th>
                                            <th>Autor</th>
                                            <th>Ementa/Descrição</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dadosPagina.map((item, index) => (
                                            <tr
                                                key={index}
                                                onClick={() => window.open(gerarUrlDetalhes(item), '_blank')}
                                                style={{ cursor: 'pointer' }}
                                                className="clickable-row"
                                            >
                                                <td style={{ width: '10%' }}>
                                                    <Badge bg={getTipoBadgeClass(item.tipo)}>
                                                        {getTipoNome(item.tipo)}
                                                    </Badge>
                                                </td>
                                                <td style={{ width: '15%' }}>
                                                    <strong>{item.dados.numero}/{item.dados.ano}</strong>
                                                    {item.dados.dataCriacao !== 'N/A' && (
                                                        <div>
                                                            <small className="text-muted">
                                                                {formatarData(item.dados.dataCriacao)}
                                                            </small>
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ width: '18%' }}>
                                                    {item.dados.autor !== 'N/A' ? item.dados.autor : '-'}
                                                </td>
                                                <td style={{ width: '45%' }}>
                                                    <div>
                                                        {truncateText(decodeHTMLEntities(item.dados.ementa), 200)}
                                                    </div>
                                                    {item.dados.subtipo && item.dados.subtipo !== 'N/A' && item.dados.subtipo.trim() !== '' && (
                                                        <div className="mt-1">
                                                            <small className="text-muted">
                                                                <em>Subtipo: {item.dados.subtipo}</em>
                                                            </small>
                                                        </div>
                                                    )}
                                                 </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    )}

                    {/* No results */}
                    {dadosFiltrados.length === 0 && (
                        <Col xs="12" className="text-center py-5">
                            <p className="text-muted">Nenhum resultado encontrado.</p>
                        </Col>
                    )}

                    {/* Pagination Info */}
                    {dadosFiltrados.length > 0 && (
                        <Col xs="12" className="text-center mt-3 mb-2">
                            <small className="text-muted">
                                Mostrando {((paginaAtual - 1) * itensPorPagina) + 1} a {Math.min(paginaAtual * itensPorPagina, dadosFiltrados.length)} de {dadosFiltrados.length} resultados
                                {totalPaginas > 1 && ` (Página ${paginaAtual} de ${totalPaginas})`}
                            </small>
                        </Col>
                    )}

                    {/* Pagination */}
                    {totalPaginas > 1 && (
                        <Col xs="12" className="d-flex justify-content-center mt-2">
                            <Pagination style={{
                                '--bs-pagination-color': '#011b53',
                                '--bs-pagination-bg': '#ffffff',
                                '--bs-pagination-border-color': '#dee2e6',
                                '--bs-pagination-hover-color': '#ffffff',
                                '--bs-pagination-hover-bg': '#011b53',
                                '--bs-pagination-hover-border-color': '#011b53',
                                '--bs-pagination-focus-color': '#ffffff',
                                '--bs-pagination-focus-bg': '#011b53',
                                '--bs-pagination-focus-border-color': '#011b53',
                                '--bs-pagination-active-color': '#ffffff',
                                '--bs-pagination-active-bg': '#011b53',
                                '--bs-pagination-active-border-color': '#011b53',
                                '--bs-pagination-disabled-color': '#6c757d',
                                '--bs-pagination-disabled-bg': '#ffffff',
                                '--bs-pagination-disabled-border-color': '#dee2e6'
                            }}>
                                <Pagination.First
                                    disabled={paginaAtual === 1}
                                    onClick={() => irParaPagina(1)}
                                />
                                <Pagination.Prev
                                    disabled={paginaAtual === 1}
                                    onClick={() => trocarPagina(-1)}
                                />

                                {gerarNumerosPaginas().map((pagina, index) => {
                                    if (pagina === '...') {
                                        return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />;
                                    }
                                    return (
                                        <Pagination.Item
                                            key={pagina}
                                            active={pagina === paginaAtual}
                                            onClick={() => irParaPagina(pagina)}
                                        >
                                            {pagina}
                                        </Pagination.Item>
                                    );
                                })}

                                <Pagination.Next
                                    disabled={paginaAtual === totalPaginas}
                                    onClick={() => trocarPagina(1)}
                                />
                                <Pagination.Last
                                    disabled={paginaAtual === totalPaginas}
                                    onClick={() => irParaPagina(totalPaginas)}
                                />
                            </Pagination>
                        </Col>
                    )}
                </>
            )}

            {/* Error Message */}
            {error && (
                <Col xs="12">
                    <div className="alert alert-danger">
                        <strong>Erro:</strong> {error}
                    </div>
                </Col>
            )}
        </Row>
    );
}
